/**
 * Thin fetch wrapper.
 *
 * The access token lives in memory only — never localStorage, so an XSS bug
 * can't read it out. Durability comes from the httpOnly refresh cookie: on a
 * cold load the app calls /auth/refresh once to mint a fresh access token.
 */

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /** Set for FormData uploads so we don't stamp a JSON content-type. */
  raw?: boolean;
  /** Internal: stops refresh recursion. */
  _retried?: boolean;
};

/**
 * In-flight refresh, shared by every caller.
 *
 * Refreshing ROTATES the token: the presented one is revoked and a new one
 * issued. So two concurrent refreshes are fatal — the second presents a token
 * the first just revoked, gets a 401, and logs the user out. That happens in
 * practice whenever two requests 401 at the same moment, and every time in dev
 * because StrictMode double-invokes effects. Collapsing them into one promise
 * is what makes rotation safe.
 */
let refreshInFlight: Promise<RefreshResult | null> | null = null;

type RefreshResult = { accessToken: string; user: unknown };

function refreshAccessToken(): Promise<RefreshResult | null> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) return null;
      const data = (await res.json()) as RefreshResult;
      accessToken = data.accessToken;
      return data;
    } catch {
      return null;
    } finally {
      // Release on the next tick so callers that awaited this exact promise
      // have all resolved before a new refresh can start.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, raw, _retried, headers, ...rest } = options;

  const finalHeaders: Record<string, string> = {
    /*
     * Only declare a JSON content-type when there is actually a JSON body.
     *
     * Fastify rejects `content-type: application/json` with an empty body as a
     * 400, so sending it unconditionally broke every bodyless POST — logout,
     * insight apply/dismiss, and the AI connection test. Logout failing was
     * invisible because the client clears its own state in a `finally`, so the
     * UI signed you out while the server session stayed alive.
     */
    ...(body !== undefined && !raw ? { 'content-type': 'application/json' } : {}),
    ...((headers as Record<string, string>) ?? {}),
  };
  if (accessToken) finalHeaders.authorization = `Bearer ${accessToken}`;

  const res = await fetch(`/api${path}`, {
    ...rest,
    headers: finalHeaders,
    credentials: 'include',
    body: body === undefined ? undefined : raw ? (body as BodyInit) : JSON.stringify(body),
  });

  // One transparent refresh-and-retry on a 401, so a 15-minute access token
  // expiring mid-session is invisible to the user.
  if (res.status === 401 && !_retried && path !== '/auth/refresh') {
    if (await refreshAccessToken()) {
      return api<T>(path, { ...options, _retried: true });
    }
    accessToken = null;
    onUnauthorized?.();
  }


  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : {};

  if (!res.ok) {
    const err = payload as { error?: string; message?: string };
    throw new ApiError(err.message ?? 'Something went wrong.', res.status, err.error ?? 'unknown');
  }

  return payload as T;
}

export const bootstrapSession = refreshAccessToken;
